/**
 * @file Установка туннеля и проверка доступности хранилища.
 *
 * discovery сообщает о соединении, но только успешный /health разрешает
 * работать с файлами. Общий срок подключения включает запрос создания,
 * опрос discovery, сетевые повторы и первую проверку /health.
 */
import { abortableDelay } from './abort.ts'
import { makeRequestWithRetry, makeTunnelRequest } from './request.ts'

export type TunnelConnectionPhase = 'discovery' | 'connecting' | 'health'

async function connectionExists(
    boxId: string,
    signal: AbortSignal,
): Promise<boolean> {
    const response = await makeRequestWithRetry({
        path: '/user-api/tunnel-discovery/router/tunnel-client/connection/http-internal',
        opts: {
            signal,
            cache: 'no-store',
            headers: { 'X-Tunnel-Client-ID': boxId },
        },
    })
    if (!response.ok) throw new Error('Tunnel discovery failed')

    // По текущему протоколу 200 означает готовность; другие успешные
    // ответы означают, что соединение ещё ожидается. Тело здесь не используется.
    void response.body?.cancel().catch(() => {})
    return response.status === 200
}

export async function checkTunnelHealth(
    boxId: string,
    token: string,
    signal: AbortSignal,
): Promise<void> {
    const bounded = AbortSignal.any([signal, AbortSignal.timeout(30_000)])
    const response = await makeTunnelRequest({
        path: '/health',
        boxID: boxId,
        token,
        retryCount: 2,
        opts: { signal: bounded, cache: 'no-store' },
    })
    bounded.throwIfAborted()
    void response.body?.cancel().catch(() => {})
    if (!response.ok) throw new Error('Storage health check failed')
}

export async function connectTunnel(
    boxId: string,
    token: string,
    signal: AbortSignal,
    {
        timeoutMs = 60_000,
        onPhase = () => {},
    }: {
        timeoutMs?: number
        onPhase?: (phase: TunnelConnectionPhase) => void
    } = {},
): Promise<void> {
    const bounded = AbortSignal.any([signal, AbortSignal.timeout(timeoutMs)])
    bounded.throwIfAborted()
    onPhase('discovery')

    if (!(await connectionExists(boxId, bounded))) {
        bounded.throwIfAborted()
        onPhase('connecting')
        // Повтор этого POST допустим по существующему контракту discovery:
        // повторная просьба установить то же соединение возвращает 204.
        const response = await makeRequestWithRetry({
            path: '/user-api/tunnel-discovery/tunnel-client/actions/connection-request',
            opts: {
                method: 'POST',
                signal: bounded,
                headers: { 'X-Tunnel-Client-ID': boxId },
            },
        })
        if (!response.ok) throw new Error('Tunnel connection request failed')
        void response.body?.cancel().catch(() => {})

        // Пауза и все чтения используют тот же deadline: ожидание не бесконечно.
        while (!(await connectionExists(boxId, bounded))) {
            await abortableDelay(1000, bounded)
        }
    }

    bounded.throwIfAborted()
    onPhase('health')
    await checkTunnelHealth(boxId, token, bounded)
}
